import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Loader2, Plus, Star, Trash2, Eye, Code } from "lucide-react";
import {
    listMailTemplates,
    createMailTemplate,
    updateMailTemplate,
    deleteMailTemplate,
    mergeMailVars
} from "../../services/crmServices/mailService";

const empty = () => ({ _id: null, name: "", category: "General", subject: "", html: "", isFavorite: false });

const PREVIEW = {
    firstName: "John",
    lastName: "Doe",
    email: "john.doe@example.com",
    phone: "+1 555 0100",
    country: "United Kingdom",
    city: "London",
    source: "Website",
    status: "New"
};

const TAGS = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "country",
    "city",
    "source",
    "status"
];

export function MailTemplatesPanel() {
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState(empty());
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("html");
    const editorRef = useRef(null);
    const htmlRef = useRef(null);

    const load = async () => {
        const res = await listMailTemplates();
        setRows(res.data || []);
    };

    useEffect(() => {
        load().catch((err) => toast.error(err.response?.data?.message || "Failed to load templates"));
    }, []);

    useEffect(() => {
        if (mode === "visual" && editorRef.current) {
            editorRef.current.innerHTML = form.html || "";
        }
    }, [mode, form._id]);

    const setHtml = (html) => setForm((f) => ({ ...f, html }));

    const syncVisual = () => {
        if (mode === "visual" && editorRef.current) {
            setHtml(editorRef.current.innerHTML);
        }
    };

    const insertTag = (key) => {
        const token = `{{${key}}}`;
        if (mode === "visual" && editorRef.current) {
            editorRef.current.focus();
            document.execCommand("insertText", false, token);
            setHtml(editorRef.current.innerHTML);
            return;
        }
        const el = htmlRef.current;
        if (el) {
            const start = el.selectionStart ?? form.html.length;
            const end = el.selectionEnd ?? start;
            const next = form.html.slice(0, start) + token + form.html.slice(end);
            setHtml(next);
            requestAnimationFrame(() => {
                el.focus();
                const pos = start + token.length;
                el.setSelectionRange(pos, pos);
            });
        } else {
            setHtml((form.html || "") + token);
        }
    };

    const save = async () => {
        syncVisual();
        const html = mode === "visual" && editorRef.current ? editorRef.current.innerHTML : form.html;
        if (!form.name || !form.subject || !html) return toast.error("Name, subject and body are required");
        setSaving(true);
        try {
            const payload = { ...form, html };
            const res = form._id ? await updateMailTemplate(form._id, payload) : await createMailTemplate(payload);
            if (res.status !== 200) throw new Error(res.message);
            toast.success("Template saved");
            setForm(empty());
            setMode("html");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    const previewHtml = mergeMailVars(form.html, PREVIEW);
    const previewSubject = mergeMailVars(form.subject, PREVIEW);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 h-full min-h-0">
            <div className="lg:col-span-2 space-y-2 overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">My templates</h3>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            setForm(empty());
                            setMode("html");
                        }}
                    >
                        <Plus className="h-3.5 w-3.5 mr-1" /> New
                    </Button>
                </div>
                {rows.map((t) => (
                    <button
                        key={t._id}
                        type="button"
                        onClick={() => {
                            setForm({ ...t });
                            setMode("html");
                        }}
                        className={`w-full text-left rounded-lg border px-3 py-2 ${form._id === t._id ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{t.name}</span>
                            {t.isFavorite && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                            {t.category} · {t.subject}
                        </p>
                    </button>
                ))}
                {rows.length === 0 && (
                    <p className="text-sm text-gray-500">Paste HTML from any builder, or write it here. Preview updates as you type.</p>
                )}
            </div>

            <div className="lg:col-span-3 overflow-y-auto bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Name</Label>
                        <Input className="h-9 mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Welcome" />
                    </div>
                    <div>
                        <Label className="text-xs">Category</Label>
                        <Input className="h-9 mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs">Subject</Label>
                        <Input
                            className="h-9 mt-1"
                            value={form.subject}
                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            placeholder="Hi {{firstName}}, your account is ready"
                        />
                        {form.subject.trim() && (
                            <p className="text-[11px] text-gray-500 mt-1">
                                Preview: <span className="text-gray-800 font-medium">{previewSubject || "(subject)"}</span>
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            className={`inline-flex items-center px-2 py-1 rounded text-xs ${mode === "html" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}
                            onClick={() => {
                                syncVisual();
                                setMode("html");
                            }}
                        >
                            <Code className="h-3 w-3 mr-1" /> HTML
                        </button>
                        <button
                            type="button"
                            className={`inline-flex items-center px-2 py-1 rounded text-xs ${mode === "visual" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}
                            onClick={() => setMode("visual")}
                        >
                            Write
                        </button>
                    </div>
                    <span className="text-[11px] text-gray-400 inline-flex items-center">
                        <Eye className="h-3 w-3 mr-1" /> Live preview on the right
                    </span>
                </div>

                <div className="flex flex-wrap gap-1">
                    {TAGS.map((key) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => insertTag(key)}
                            className="text-[11px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100"
                            title="Insert into the body"
                        >
                            {`{{${key}}}`}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    {mode === "html" ? (
                        <Textarea
                            ref={htmlRef}
                            className="font-mono text-xs min-h-[320px]"
                            value={form.html}
                            onChange={(e) => setHtml(e.target.value)}
                            placeholder={'<table width="100%"><tr><td>Hi {{firstName}},</td></tr></table>'}
                            spellCheck={false}
                        />
                    ) : (
                        <div
                            ref={editorRef}
                            className="min-h-[320px] rounded-md border border-input bg-white px-3 py-2 text-sm outline-none overflow-y-auto"
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={syncVisual}
                        />
                    )}

                    <div className="border border-gray-200 rounded-md overflow-hidden bg-white min-h-[320px] flex flex-col">
                        <div className="px-3 py-1.5 border-b bg-gray-50 text-[11px] text-gray-500 truncate">
                            {previewSubject || "Subject preview"}
                        </div>
                        {form.html.trim() ? (
                            <iframe
                                title="Email preview"
                                srcDoc={previewHtml}
                                className="w-full flex-1 min-h-[280px] bg-white"
                                sandbox=""
                            />
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-xs text-gray-400 p-4 text-center">
                                Paste HTML on the left. Tags like {"{{firstName}}"} become sample lead data here.
                            </div>
                        )}
                    </div>
                </div>

                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.isFavorite} onChange={(e) => setForm({ ...form, isFavorite: e.target.checked })} />
                    Favorite
                </label>

                <div className="flex gap-2">
                    <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700">
                        {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save template
                    </Button>
                    {form._id && (
                        <Button
                            variant="outline"
                            className="text-red-600"
                            onClick={async () => {
                                await deleteMailTemplate(form._id);
                                setForm(empty());
                                load();
                            }}
                        >
                            <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
